import type { ComponentType, JSX, ReactElement, ReactNode, Ref, SVGProps } from 'react';
import { forwardRef, isValidElement, memo } from 'react';

import clsx from 'clsx';

/**
 * Drop-in replacement for MUI's `SvgIcon` (and `createSvgIcon`), kept to the props this codebase
 * uses so the icon set no longer pulls in `@mui/material`, `@mui/system` and Emotion.
 *
 * Matches MUI's rendering: a 1em × 1em `currentColor` svg sized by `fontSize` (small 1.25rem,
 * medium 1.5rem, large 2.1875rem, or inherit), `htmlColor` as the svg's `color` attribute, `aria-hidden` unless
 * `titleAccess` gives it an accessible name, and — for an `<svg>` child — the child's attributes
 * merged onto the root with its own fill left alone.
 */

export type SvgIconFontSize = 'inherit' | 'small' | 'medium' | 'large';

export interface SvgIconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'fontSize'> {
    children?: ReactNode;
    fontSize?: SvgIconFontSize;
    /** Applied as the svg's `color` attribute, so `currentColor` fills resolve to it. */
    htmlColor?: string;
    /** Accessible name: renders a `<title>` and exposes the icon as `role="img"`. */
    titleAccess?: string;
    /** Leave `viewBox` to the `<svg>` child instead of defaulting it. */
    inheritViewBox?: boolean;
}

/**
 * Base styles live in plain `.svg-icon*` rules at the end of `styles/globals.css`, after Tailwind's
 * utilities, rather than in utility classes or inline styles. That reproduces MUI's precedence
 * exactly: Emotion injected its single-class rules after the Tailwind stylesheet, so they beat a
 * caller's same-specificity utility (`size-4` on an icon was ignored) but lost to higher-specificity
 * parent rules (`[&_svg]:…`) and to inline `style` — and changing that would resize icons app-wide.
 */

export const SvgIcon = forwardRef(function SvgIcon(
    {
        children,
        className,
        // MUI treats `color` as a theme-palette key and never forwards it to the DOM; nothing here
        // uses palette colors, so it is swallowed rather than becoming the svg's `color` attribute.
        color: _paletteColor,
        fontSize = 'medium',
        htmlColor,
        titleAccess,
        inheritViewBox = false,
        viewBox = '0 0 24 24',
        ...rest
    }: SvgIconProps,
    ref: Ref<SVGSVGElement>,
): JSX.Element {
    const svgChild = isValidElement<SVGProps<SVGSVGElement>>(children) && children.type === 'svg' ? children : null;
    const { children: svgChildContent, ...svgChildProps } = svgChild?.props ?? {};

    // Attribute order mirrors MUI's: its own props, then `viewBox`, then the caller's, then an `<svg>`
    // child's attributes on top of everything.
    return (
        <svg
            className={clsx('svg-icon', `svg-icon-${fontSize}`, !svgChild && 'svg-icon-fill', className)}
            focusable="false"
            color={htmlColor}
            aria-hidden={titleAccess ? undefined : true}
            role={titleAccess ? 'img' : undefined}
            ref={ref}
            {...(inheritViewBox ? null : { viewBox })}
            {...rest}
            {...svgChildProps}
        >
            {svgChild ? svgChildContent : children}
            {titleAccess ? <title>{titleAccess}</title> : null}
        </svg>
    );
});

/** An icon component made by `createSvgIcon`. */
export type SvgIconComponent = ComponentType<SvgIconProps & { ref?: Ref<SVGSVGElement> }>;

/**
 * Same contract as MUI's `createSvgIcon`, including the `data-testid="<Name>Icon"` tests query by.
 *
 * The Material icons in `./material/` were generated from `@mui/icons-material`, which is no longer
 * installed. To add one, copy its path data from Google's Material Icons (fonts.google.com/icons,
 * 24px, or `@mui/icons-material/esm/<Name>.js` via `npm pack`) into a new `material/<Name>.tsx`
 * following the existing files, and keep the MUI name so its test id matches.
 */
export function createSvgIcon(path: ReactElement | ReactElement[], displayName: string): SvgIconComponent {
    const Icon = forwardRef<SVGSVGElement, SvgIconProps>(function Icon(props, ref) {
        return (
            <SvgIcon data-testid={`${displayName}Icon`} ref={ref} {...props}>
                {path}
            </SvgIcon>
        );
    });
    Icon.displayName = `${displayName}Icon`;
    return memo(Icon) as unknown as SvgIconComponent;
}

export default SvgIcon;
